import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as padStart from 'lodash/padStart';

import { AirportNavaid, NavaidType } from '../models/airport-navaid.model';
import { isNumeric } from '../../util/helpers';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { EditNavaidComponent } from '../edit-navaid/edit-navaid.component';
import { MatDialog, MatDialogConfig, MatSnackBar } from '@angular/material';
import { DeleteNavaidComponent } from '../delete-navaid/delete-navaid.component';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { Source } from '../../shared/models/source.model';


@Component({
  selector: 'app-navaid',
  templateUrl: './navaid.component.html',
  styleUrls: ['./navaid.component.scss']
})
export class NavaidComponent implements OnInit {
  @Input() navaid: AirportNavaid;
  @Output() navaidDeleted = new EventEmitter<number>();
  editModeSet: boolean;
  isFlightDeckAdmin: boolean;
  isIslandSource: boolean;

  constructor(private _toggleService: EditToggleService,
              private _editNavaidDialog: MatDialog,
              private _deleteNavaidDialog: MatDialog,
              private _snackBar: MatSnackBar,
              private _businessManagerService: BusinessManagerService) {
  }

  async ngOnInit() {
    this.editModeSet = this._toggleService.getState();
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
    this.isFlightDeckAdmin = await this._businessManagerService.isFlightDeckAdmin();
    this.isIslandSource = this.navaid.sourceId === Source.CaribbeanGuide || this.navaid.sourceId === Source.BahamasGuide;
  }

  /**
   * Returns formatted bearing from navaid to airport
   */
  get bearing(): string {
    return isNumeric(this.navaid.radial) ? padStart(this.navaid.radial, 3, '0') : null;
  }

  /**
   * Returns the icon to use for the navaid
   * @returns {string}
   */
  get icon(): string {
    switch (this.navaid.type) {
      case NavaidType.VOR:
        return 'navaids:vor';
      case NavaidType.NDB:
      case NavaidType.MarineNDB:
        return 'navaids:ndb';
      case NavaidType.NDB_DME:
        return 'navaids:ndb-dme';
      case NavaidType.VOR_DME:
        return 'navaids:vor-dme';
      case NavaidType.VORTAC:
        return 'navaids:vortac';
      case NavaidType.VOT:
        return 'navaids:vot';
      default:
        return null;
    }
  }

  /**
   * Returns formatted navaid type
   */
  get type(): string {
    return AirportNavaid.formatNavaidType(this.navaid.type);
  }

  openEditNavaidDialog(navaid: AirportNavaid) {
    let ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    const dialogRef = this._editNavaidDialog.open(EditNavaidComponent, {
      disableClose: false,
      autoFocus: true,
      data: {
        isNew: false,
        navaid: navaid
      }
    });
    dialogRef.afterClosed().subscribe(isUpdated => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) {
        return;
      }
      if (isUpdated.success) {
        this._snackBar.open('Navaid', 'Updated', {
          duration: 2000
        });
      } else {
        if (isUpdated.error != null) {
          ERROR_MSG = isUpdated.error.message;
        }
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deleteNavaid(navaid: AirportNavaid) {
    const dialogConfig = new MatDialogConfig();
    let ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      navaidId: navaid.navaidId
    };
    const dialogRef = this._deleteNavaidDialog.open(DeleteNavaidComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this.navaidDeleted.emit(navaid.navaidId);
        this._snackBar.open('Navaid', 'Deleted', {
          duration: 2000
        });
      } else {
        if (isUpdated.error != null) {
          ERROR_MSG = isUpdated.error.message;
        }
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }
}
